import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SeoMeta from "../../components/SeoMeta";
import PageTitle from "./components/PageTitle";
import COAInputForm from "./components/COAInputForm";
import { useLoading } from "../../contexts/LoadingContext";

// Get locale from cookie
const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};



function COAVerification() {
  const { slug } = useParams(); // optional if using slugs
  const { t, i18n } = useTranslation();
  const [otp, setOtp] = useState("");
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState(null);
  const { setIsLoading } = useLoading();
  const locale = getLocale();

  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;

  const handleSearch = async () => {
    if (!otp || otp.length !== 16) {
      setError(t("coaVerification.invalidCode", "Please enter a valid 16-digit code."));
      return;
    }

    const path = `/api/fine-art-prints?filters[printID][$eq]=${otp}&locale=${locale}&populate=deep`;
    const url = new URL(STRAPI_URL + path);

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${STRAPI_TOKEN}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result?.data?.[0];
        if (!data) {
          setError(t("coaVerification.notFound", "No certificate found for the entered code."));
        } else {
          setPageData(data);
        }
      } else {
        const text = await response.text();
        setError(`${t("coaVerification.fetchError", "Error fetching data")}: ${response.status} - ${text}`);
      }
    } catch (err) {
      console.error("❌ Fetch error:", err.message);
      setError(t("coaVerification.networkError", "Network error. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setOtp(e.target.value.replace(/\s/g, "").toUpperCase());
  };


  return (
    <>
      <SeoMeta
        lang={i18n.language}
        title={t("coaVerification.SeoTitle", "Certificate of Authenticity Verification")}
        description={t(
          "coaVerification.SeoDescription",
          "Enter your 16-digit code to validate the authenticity of your art print."
        )}
        keywords={t(
          "coaVerification.SeoKeywords",
          "COA, print verification, fine art certificate, authenticity"
        )}
        robots="noindex, nofollow"
      />


      <section className="py-15 py-xl-20 max-w-2xl mx-auto">
        <PageTitle
          title={t("coaVerification.title", "Verify Your Print")}
          subtitle={t(
            "coaVerification.subtitle",
            "Enter the 16-digit code from your Certificate of Authenticity or label"
          )}
        />


        <COAInputForm
          otp={otp}
          onChange={handleChange}
          onSubmit={handleSearch}
          error={error}
        />
      </section>
    </>
  );
}

export default COAVerification;
